<template>
  <div class="journey-list">
    <div v-for="(event, index) in sortedJourneys" :key="event.guid" class="journey-card">
      <div class="journey-card-header">
        <div class="journey-header-container">
          <div class="header-badge">
            <span class="header-label">Journey</span>
            <span class="header-value">#{{ index + 1 }}</span>
          </div>
          <div class="header-badge">
            <span class="header-label">UID</span>
            <span class="header-value">{{ event.uid }}</span>
          </div>
          <div class="header-badge">
            <span class="header-label">Vehicle</span>
            <span class="header-value">{{ vehicleRef(event) }}</span>
          </div>
          <button class="copy-details-btn" @click="copyJourneyDetails(event, index)" title="Copy to clipboard">
            📑
          </button>
        </div>
        <div class="journey-meta">
          <div>{{ formateDate(event.time) }}</div>
          <div>{{ lineName(event) }}</div>
          <div class="guid-row">
            <span class="guid">{{ guid(event) }}</span>
            <button class="copy-btn" @click="copyToClipboard(guid(event))" title="Copy GUID">
              📋
            </button>
          </div>
        </div>
      </div>

      <div class="journey-card-content">
        <PIDJourneyData :jguid="event.guid" />
        <PIDJourneyEventOverview :jguid="event.guid" />
      </div>
    </div>
  </div>
</template>

<script setup>

/*
fields in newjourney evtdata:
            "Destination": "Bury St Edmunds - Thetford",
            "vehicleRef": "CS74BUS",
            "VehicleJourneyCode": "11320",
            "LineName": "84",
            "OperatorRef": "CSVC",
            "DirectionRef": "inbound",
            "Origin": "",
            "Description": "Bury St Edmunds - Thetford",
            "stops": Array of stops

*/
import { inject, computed } from 'vue';
import PIDJourneyData from '../components/PIDJourneyData.vue';
import PIDJourneyEventOverview from '../components/PIDJourneyEventOverview.vue';

const dataStore = inject('datastore');

const sortedJourneys = computed(() => {
  const events = Object.values(dataStore.pidnewjourneyevents);
  return events.sort((a, b) => a.time - b.time);
});

function vehicleRef(event) {
  return event.evtdata?.vehicleRef || 'N/A';
}

function formateDate(time) {
  time *= 1000
  const date = new Date(time);
  return "Set Time  :  " + date.toLocaleString(undefined, {
    weekday: 'long',  // e.g., "Friday"
    year: 'numeric',  // e.g., "2024"
    month: 'long',    // e.g., "November"
    day: 'numeric',   // e.g., "8"
    hour: '2-digit',  // e.g., "11" (for "11 AM")
    minute: '2-digit', // e.g., "30"
    hour12: true      // 12-hour format
  });
}

function lineName(event) {

  if (!event.evtdata.LineName || event.evtdata.LineName.length == 0) {
    return "No Journey Set"
  }

  let name = event.evtdata.LineName + "  :  "
  if (event.evtdata.stops && event.evtdata.stops.length > 0) {
    name += "  :  " + event.evtdata.stops[0].commonname + " - " + event.evtdata.stops[event.evtdata.stops.length - 1].commonname
  } else {
    name += "No Stops"
  }

  return name;
}

function guid(event) {
  return event.guid
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    // You could add a toast/notification here
    console.log('GUID copied to clipboard');
  } catch (err) {
    console.error('Failed to copy: ', err);
  }
}

async function copyJourneyDetails(event, index) {
  const details = `**Journey #${index + 1}**
**UID:** ${event.uid}
**Vehicle:** ${vehicleRef(event)}
**Time:** ${formateDate(event.time).replace('Set Time  :  ', '')}
**Line:** ${lineName(event)}
**GUID:** ${guid(event)}`;

  await copyToClipboard(details);
}




</script>

<style scoped>
.journey-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px;
}

.journey-card {
  background-color: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.journey-card-header {
  background-color: #e9ecef;
  padding: 15px;
  border-bottom: 1px solid #dee2e6;
}

.journey-meta {
  margin-top: 10px;
  font-size: 0.9em;
  color: #555;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.guid {
  font-family: monospace;
  font-size: 0.85em;
  color: #777;
}

.guid-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.copy-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2em;
  padding: 0;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.copy-btn:hover {
  opacity: 1;
}

.copy-details-btn {
  margin-left: auto;
  background-color: #ffffff;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.9em;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.copy-details-btn:hover {
  background-color: #f0f0f0;
  color: #222;
  border-color: #bbb;
}

.journey-card-content {
  padding: 15px;
}

.journey-header-container {
  display: flex;
  gap: 15px;
  align-items: center;
  margin-bottom: 10px;
}

.header-badge {
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #dcdcdc;
  min-width: 90px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.header-label {
  font-size: 0.7em;
  text-transform: uppercase;
  color: #666;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.header-value {
  font-size: 1.2em;
  font-weight: 700;
  color: #222;
}
</style>